/* eslint-disable camelcase */
import type { SubscriptionUpdateAction } from '../confirmation/ApplyNewSubscriptionConfirmation';

export type BillingCycle = 'month' | 'year';

export type Tier = {
  id: string
  traffic_limit: number
  domains_limit: number
  billing_cycle: BillingCycle
  price?: number
};

export const catalogMaxTrafficLimit = (catalogTiers: Tier[]): number =>
  (catalogTiers.length === 0 ? 0 : Math.max(...catalogTiers.map(tier => tier.traffic_limit)));

export const findTierByLimits = (
  tiers: Tier[],
  trafficLimit: number,
  domainsLimit: number,
  billingCycle: BillingCycle,
): Tier | undefined =>
  tiers.find(tier =>
    tier.traffic_limit === trafficLimit
    && tier.domains_limit === domainsLimit
    && tier.billing_cycle === billingCycle);

export const tierSelectorMatchesSubscribed = (selector: Tier, subscribed: Tier): boolean =>
  selector.traffic_limit === subscribed.traffic_limit
  && selector.billing_cycle === subscribed.billing_cycle
  && selector.domains_limit === subscribed.domains_limit;

export const getAction = (
  newTier?: Tier,
  currentTier?: Tier,
  /**
   * Prefer the real subscribed unit price when available.
   * In multi-product flows the UI may only have a limits-matched tier from another product's
   * catalog, whose price is not what the customer currently pays.
   */
  subscribedPrice?: string | null,
): SubscriptionUpdateAction => {
  const newPrice = Number(newTier?.price) || 0;
  const hasSubscribedPrice = typeof subscribedPrice === 'string' && subscribedPrice !== '';
  const currentPrice = hasSubscribedPrice
    ? (Number(subscribedPrice) || 0)
    : (Number(currentTier?.price) || 0);

  return newPrice >= currentPrice ? 'upgrade' : 'downgrade';
};

export const findNearestHigherTier = (
  tiers: Tier[],
  trafficToCompare: number,
  billingCycle?: BillingCycle,
  domainsLimit?: number,
  currentTier?: Tier,
): Tier | null => {
  const filteredTiers = tiers.filter(
    tier =>
      tier.domains_limit === domainsLimit
      && tier.traffic_limit > trafficToCompare
      && (!billingCycle || tier.billing_cycle === billingCycle),
  );

  const sortedTiers = filteredTiers.sort((a, b) => a.traffic_limit - b.traffic_limit);

  if (
    currentTier
    && sortedTiers[0]
    && currentTier.traffic_limit >= sortedTiers[0].traffic_limit
  ) {
    return null;
  }

  return sortedTiers[0];
};

/** Smallest catalog traffic_limit strictly above usage (for current billing/domains). Ignores subscribed tier — use for selectable minimum. */
export const findSmallestTrafficLimitAboveUsage = (
  tiers: Tier[],
  trafficToCompare: number,
  billingCycle?: BillingCycle,
  domainsLimit?: number,
): number | undefined => {
  const filteredTiers = tiers.filter(
    tier =>
      tier.domains_limit === domainsLimit
      && tier.traffic_limit > trafficToCompare
      && (!billingCycle || tier.billing_cycle === billingCycle),
  );
  if (filteredTiers.length === 0) return undefined;
  return filteredTiers.sort((a, b) => a.traffic_limit - b.traffic_limit)[0]?.traffic_limit;
};

/** Enterprise upsell cue: usage at/above SMB catalog ceiling, or subscribed on ceiling with usage strictly above ceiling. */
export const trafficSuggestsEnterpriseBeyondSMB = (
  catalogTiers: Tier[],
  usage: number,
  subscribedTier?: Tier | null,
): boolean => {
  const topTraffic = catalogMaxTrafficLimit(catalogTiers);
  if (topTraffic <= 0) return false;
  return (
    (typeof subscribedTier !== 'undefined' && subscribedTier !== null
      && subscribedTier.traffic_limit === topTraffic && usage > topTraffic)
    || usage >= topTraffic
  );
};

export const tierSelectorSyncedToSubscriptionForEnterpriseSMB = (
  catalog: Tier[],
  usage: number,
  selector: Tier | undefined,
  subscribed: Tier | undefined,
  subscribedPriceId: string | undefined,
): boolean => Boolean(
  subscribedPriceId && subscribed && selector
    && trafficSuggestsEnterpriseBeyondSMB(catalog, usage, subscribed)
    && tierSelectorMatchesSubscribed(selector, subscribed),
);
